// Envelope-aware API-клієнт (canonical §5.1/§5.2): успіх `{data,meta}`,
// помилка `{error:{code,message},meta}`. httponly-cookies → `credentials:"include"`.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(source: unknown, ...path: string[]): string | undefined {
  let current: unknown = source;
  for (const key of path) {
    if (!isRecord(current)) return undefined;
    current = current[key];
  }
  return typeof current === "string" ? current : undefined;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  // M19 F5: кастомні заголовки (Idempotency-Key для robot test-run).
  headers?: Record<string, string>;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: Record<string, unknown>;
}

// ── Session-expiry координація (UC-02: тихий refresh + реактивний sign-out) ──────
// Access-cookie живе ~15 хв; refresh-cookie — довше. Замість того, щоб «випадати»
// з сесії на першому ж 401 (і зависати на екрані, доки користувач не перезавантажить
// сторінку вручну), клієнт один раз тихо смикає POST /api/auth/refresh і повторює
// початковий запит. Якщо refresh теж мертвий — сповіщаємо AuthContext, який скидає
// стан у `unauthenticated`, а ProtectedRoute реактивно веде на /login.

// Ендпоінти самого auth-циклу: їхній 401 НЕ має тригерити refresh/повтор — інакше
// рекурсія (refresh→refresh) і проковтнута помилка невірних кредів на /login.
function isAuthEndpoint(path: string): boolean {
  return (
    path.startsWith("/api/auth/login") ||
    path.startsWith("/api/auth/refresh") ||
    path.startsWith("/api/auth/logout")
  );
}

// AuthContext реєструє тут скидання сесії. Один обробник — на застосунок один
// AuthProvider (повторна реєстрація просто перезаписує попередній).
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// Envelope-код 401-ї відповіді без споживання тіла основного потоку (clone для
// справжнього Response; тестові моки без clone читаються напряму — їхній text
// повторно викличний).
async function unauthorizedCode(response: Response): Promise<string | undefined> {
  try {
    const source = typeof response.clone === "function" ? response.clone() : response;
    const text = await source.text();
    return readString(text ? JSON.parse(text) : null, "error", "code");
  } catch {
    return undefined;
  }
}

// Дедуплікований refresh: десятки паралельних 401 діляться ОДНИМ /refresh-запитом.
let refreshInFlight: Promise<boolean> | null = null;
async function attemptSilentRefresh(): Promise<boolean> {
  if (refreshInFlight === null) {
    refreshInFlight = fetch(`${BASE_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}


// W1-6 (CAPACITY-8020): дзеркало backend FILE_MAX_SIZE_BYTES (file_object.py) —
// файл понад ліміт гарантовано відхилять, нема сенсу ганяти 100+ MiB у мережу.
const FILE_MAX_SIZE_BYTES = 100 * 1024 * 1024;

function assertFilesWithinLimit(form: FormData): void {
  for (const value of Array.from(form.values())) {
    if (typeof value !== "string" && value.size > FILE_MAX_SIZE_BYTES) {
      throw new ApiError(
        413,
        "FILE_TOO_LARGE",
        "Файл завеликий: ліміт 100 МіБ",
      );
    }
  }
}

export async function apiRequestEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const hasForm = options.form !== undefined;
  if (hasForm) assertFilesWithinLimit(options.form as FormData);
  const hasBody = !hasForm && options.body !== undefined;

  const sendOnce = async (): Promise<Response> => {
    try {
      return await fetch(`${BASE_URL}${path}`, {
        method: options.method ?? "GET",
        credentials: "include",
        // multipart (form) — браузер сам ставить boundary; JSON — явний content-type.
        headers: {
          ...(options.headers ?? {}),
          ...(hasBody ? { "Content-Type": "application/json" } : {}),
        },
        body: hasForm
          ? options.form
          : hasBody
            ? JSON.stringify(options.body)
            : undefined,
        signal: options.signal,
      });
    } catch {
      throw new ApiError(0, "NETWORK_ERROR", "Немає звʼязку з сервером");
    }
  };

  let response = await sendOnce();

  // 401 (окрім самих auth-ендпоінтів) → тихий refresh + один повтор; при повторному
  // 401 — глобальний sign-out (реактивний перехід на /login без перезавантаження).
  // Каскад діє ЛИШЕ для envelope-коду UNAUTHENTICATED (мертва сесія від
  // auth-шару — обробник ще не виконувався, повтор безпечний). Доменні 401
  // обробників (напр. AUTH_INVALID_CREDENTIALS від change-password) не
  // ретраяться — мутація виконалася б двічі — і не розлогінюють (VF-001).
  if (
    response.status === 401 &&
    !isAuthEndpoint(path) &&
    (await unauthorizedCode(response)) === "UNAUTHENTICATED"
  ) {
    if (await attemptSilentRefresh()) {
      response = await sendOnce();
    }
    if (
      response.status === 401 &&
      (await unauthorizedCode(response)) === "UNAUTHENTICATED"
    ) {
      unauthorizedHandler?.();
    }
  }

  if (response.status === 204) {
    return { data: undefined as T, meta: {} };
  }

  let text: string;
  try {
    text = await response.text();
  } catch {
    // обрив тіла при читанні → ApiError (не raw TypeError).
    throw new ApiError(0, "NETWORK_ERROR", "Помилка читання відповіді сервера");
  }
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }
  const requestId = readString(payload, "meta", "request_id");

  if (!response.ok) {
    const code = readString(payload, "error", "code") ?? "HTTP_ERROR";
    const message =
      readString(payload, "error", "message") ?? `Помилка ${response.status}`;
    throw new ApiError(response.status, code, message, requestId);
  }

  const meta =
    isRecord(payload) && isRecord(payload.meta) ? payload.meta : {};
  if (isRecord(payload) && "data" in payload) {
    return { data: payload.data as T, meta };
  }
  return { data: payload as T, meta };
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await apiRequestEnvelope<T>(path, options)).data;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiRequest<T>(path, { signal }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    apiRequest<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    apiRequest<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown): Promise<T> =>
    apiRequest<T>(path, { method: "PUT", body }),
  del: (path: string): Promise<void> =>
    apiRequest<void>(path, { method: "DELETE" }),
  postForm: <T>(path: string, form: FormData): Promise<T> =>
    apiRequest<T>(path, { method: "POST", form }),
};

/**
 * POST, що повертає бінарний файл (напр. test-render DOCX) — НЕ envelope.
 * Помилку backend піднімає як ApiError (JSON-тіло). Опційне JSON-`body`.
 */
export async function postBlob(
  path: string,
  body?: unknown,
): Promise<{ filename: string; blob: Blob }> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Немає звʼязку з сервером");
  }
  if (!response.ok) {
    let payload: unknown = null;
    try {
      const errText = await response.text();
      payload = errText ? JSON.parse(errText) : null;
    } catch {
      payload = null;
    }
    throw new ApiError(
      response.status,
      readString(payload, "error", "code") ?? "HTTP_ERROR",
      readString(payload, "error", "message") ?? `Помилка ${response.status}`,
      readString(payload, "meta", "request_id"),
    );
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { filename: match ? match[1] : "test-render.docx", blob };
}

/**
 * Завантаження сирого тексту (CSV-експорт) — НЕ envelope. Повертає вміст + ім'я
 * файлу з Content-Disposition; помилку backend піднімає як ApiError.
 */
export async function downloadText(
  path: string,
): Promise<{ filename: string; text: string }> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { credentials: "include" });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Немає звʼязку з сервером");
  }
  if (!response.ok) {
    let payload: unknown = null;
    try {
      const errText = await response.text();
      payload = errText ? JSON.parse(errText) : null;
    } catch {
      payload = null;
    }
    throw new ApiError(
      response.status,
      readString(payload, "error", "code") ?? "HTTP_ERROR",
      readString(payload, "error", "message") ?? `Помилка ${response.status}`,
      readString(payload, "meta", "request_id"),
    );
  }
  let text: string;
  try {
    text = await response.text();
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Помилка читання відповіді сервера");
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { filename: match ? match[1] : "export.csv", text };
}
